import React, { useEffect, useState } from "react";
import { FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import totalFleetIcon from "../../../../Assets/Icons/laptop_car_fleet.svg";
import activeIcon from "../../../../Assets/Icons/delivery_truck_bolt.svg";
import creditIcon from "../../../../Assets/Icons/credit_card_utilized.svg";
import avgSpendIcon from "../../../../Assets/Icons/order_approve.svg";
import fleetIcon from "../../../../Assets/Icons/laptop_car.svg";
import lockIcon from "../../../../Assets/Icons/lock.svg";
import totalVendorsIcon from "../../../../Assets/Icons/storefront.svg";
import activeVendorIcon from "../../../../Assets/Icons/storefront_active.svg";
import paymentIcon from "../../../../Assets/Icons/payments_spend.svg";
import fuelIcon from "../../../../Assets/Icons/fuel-icon.svg";
import GenericTable from "../../../../Components/Tables/GenericTable";
import { useDateFilter } from "../../../../Contexts/DateFilterContext";
import { getAdminReports } from "../../../../API/Admin/Reports";
import { getInitials } from "../../../../Components/InitialsAvatar";

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const InitialsAvatar = ({ name }) => (
  <span
    style={{
      width: 24,
      height: 24,
      borderRadius: "5px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 700,
      color: "#fff",
      background: getAvatarColor(name),
      flexShrink: 0,
    }}
  >
    {getInitials(name)}
  </span>
);

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const MasterReport = () => {
  const { from, to } = useDateFilter();
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const startDate = from ? from.split(" ")[0] : undefined;
        const endDate = to ? to.split(" ")[0] : undefined;

        const res = await getAdminReports("master", startDate, endDate);

        if (Math.floor(res?.status / 100) === 2) {
          setReportData(res?.data?.data || null);
        }
      } catch (err) {
        console.error("Failed to load master report:", err);
      }
    };

    fetchReport();
  }, [from, to]);

  const stats = reportData?.stats || {};

  const transactionDataSource = (reportData?.transactions || []).map(
    (txn) => ({
      key: String(txn.id),
      dateTime: `${txn.date || "-"} ${txn.time || ""}`.trim(),
      txnId: txn.txn_id || "-",
      fleetManager: txn.fleet_company_name || "-",
      driverName: txn.driver_name || "-",
      vehicle: txn.vehicle_id || "-",
      vendor: txn.fuel_vendor_name || "-",
      fuelStation: txn.fuel_vendor_station_name || "-",
      fuelType: txn.fuel_type_name || "-",
      fuelVolume: txn.volume ? `${txn.volume} L` : "-",
      fuelCost: formatCurrency(txn.amount),
      fuelCostPerL: txn.cost_per_liter ? `$${txn.cost_per_liter}` : "-",
      creditUsed:
        txn.credit_used_percent !== null && txn.credit_used_percent !== undefined
          ? `${txn.credit_used_percent}%`
          : "-",
      location: txn.location || "-",
      status: capitalize(txn.status) || "-",
    }),
  );

  const uniqueFuelTypes = [
    ...new Set(transactionDataSource.map((i) => i.fuelType)),
  ].map((f) => ({ text: f, value: f }));
  const uniqueStatus = [
    ...new Set(transactionDataSource.map((i) => i.status)),
  ].map((s) => ({ text: s, value: s }));

  const handleExport = () => {
    const exportData = transactionDataSource.map((item) => ({
      "Date/Time": item.dateTime,
      "Txn Id": item.txnId,
      "Fleet Manager": item.fleetManager,
      "Driver Name": item.driverName,
      Vehicle: item.vehicle,
      Vendor: item.vendor,
      "Fuel Station": item.fuelStation,
      "Fuel Type": item.fuelType,
      "Fuel Volume": item.fuelVolume,
      "Fuel Cost": item.fuelCost,
      "Fuel Cost Per L": item.fuelCostPerL,
      "Credit Used": item.creditUsed,
      Location: item.location,
      Status: item.status,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Master Report");
    XLSX.writeFile(workbook, "master-report.xlsx");
  };

  const transactionColumns = [
    {
      title: "Date/Time",
      dataIndex: "dateTime",
      key: "dateTime",
      sorter: (a, b) =>
        (new Date(a.dateTime).getTime() || 0) -
        (new Date(b.dateTime).getTime() || 0),
      render: (text) => {
        const [date, time] = text.split(" ");
        return (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <span>{date}</span>
            <span style={{ color: "#71717A" }}>{time}</span>
          </div>
        );
      },
    },
    {
      title: "Txn Id",
      dataIndex: "txnId",
      key: "txnId",
      enableSearch: true,
    },
    {
      title: "Fleet managers",
      dataIndex: "fleetManager",
      key: "fleetManager",
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InitialsAvatar name={text} /> {text}
        </span>
      ),
      enableSearch: true,
    },
    {
      title: "Driver Name",
      dataIndex: "driverName",
      key: "driverName",
      enableSearch: true,
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InitialsAvatar name={text} /> {text}
        </span>
      ),
    },
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      enableSearch: true,
    },
    {
      title: "Vendors",
      dataIndex: "vendor",
      key: "vendor",
      render: (text) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <InitialsAvatar name={text} />
          <span>{text}</span>
        </div>
      ),
      enableSearch: true,
    },
    {
      title: "Fuel Stations",
      dataIndex: "fuelStation",
      key: "fuelStation",
      enableSearch: true,
    },
    {
      title: "Fuel Type",
      dataIndex: "fuelType",
      key: "fuelType",
      filters: uniqueFuelTypes,
      onFilter: (value, record) => record.fuelType === value,
      render: (type) => <span className="type-badge">{type}</span>,
    },
    {
      title: "Fuel Volume",
      dataIndex: "fuelVolume",
      key: "fuelVolume",
      sorter: (a, b) =>
        (parseFloat(a.fuelVolume) || 0) - (parseFloat(b.fuelVolume) || 0),
    },
    {
      title: "Fuel Cost",
      dataIndex: "fuelCost",
      key: "fuelCost",
      sorter: (a, b) =>
        (parseFloat(a.fuelCost.replace(/[$,]/g, "")) || 0) -
        (parseFloat(b.fuelCost.replace(/[$,]/g, "")) || 0),
    },
    {
      title: "Fuel Cost Per L",
      dataIndex: "fuelCostPerL",
      key: "fuelCostPerL",
      sorter: (a, b) =>
        (parseFloat(a.fuelCostPerL.replace(/[$,]/g, "")) || 0) -
        (parseFloat(b.fuelCostPerL.replace(/[$,]/g, "")) || 0),
    },
    {
      title: "Credit Used",
      dataIndex: "creditUsed",
      key: "creditUsed",
      sorter: (a, b) =>
        (parseFloat(a.creditUsed) || 0) - (parseFloat(b.creditUsed) || 0),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      enableSearch: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: uniqueStatus,
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span className={`status-tag ${status.toLowerCase()}`}>{status}</span>
      ),
    },
  ];

  return (
    <div className="master-report">
      <div className="row">
        <div className="tabs">
          <GenericStatCard
            title="Total Fleets"
            value={stats.total_fleets?.value ?? stats.total_fleets ?? 0}
            subtitle="vs. last month"
            icon={totalFleetIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Active"
            value={stats.active_fleets?.value ?? stats.active_fleets ?? 0}
            subtitle="vs. last month"
            icon={activeIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Spend"
            value={formatCurrency(stats.total_spend?.value)}
            {...(stats.total_spend?.percentage != null && {
              percentage: `${stats.total_spend.percentage}`,
            })}
            isIncrease={stats.total_spend?.is_increase}
            subtitle="vs. last month"
            icon={fleetIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Credit Utilized"
            value={`${stats.credit_utilized_percent?.value ?? 0}%`}
            {...(stats.credit_utilized_percent?.percentage != null && {
              percentage: `${stats.credit_utilized_percent.percentage}`,
            })}
            isIncrease={stats.credit_utilized_percent?.is_increase}
            subtitle="vs. last month"
            icon={creditIcon}
          />
        </div>
      </div>
      <div className="row">
        <div className="tabs">
          <GenericStatCard
            title="Avg Spend"
            value={formatCurrency(stats.avg_spend?.value ?? stats.avg_spend)}
            subtitle="vs. last month"
            icon={avgSpendIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Locked Count"
            value={stats.locked_count?.value ?? stats.locked_count ?? 0}
            subtitle="Overall Locked"
            icon={lockIcon}
          />
        </div>
      </div>
      <div className="row">
        <div className="tabs">
          <GenericStatCard
            title="Total Vendors"
            value={stats.total_vendors?.value ?? stats.total_vendors ?? 0}
            subtitle="vs. last month"
            icon={totalVendorsIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Active Vendors"
            value={stats.active_vendors?.value ?? stats.active_vendors ?? 0}
            subtitle="vs. last month"
            icon={activeVendorIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Fuel Volume"
            value={`${stats.total_fuel_volume?.value ?? 0} L`}
            {...(stats.total_fuel_volume?.percentage != null && {
              percentage: `${stats.total_fuel_volume.percentage}`,
            })}
            isIncrease={stats.total_fuel_volume?.is_increase}
            subtitle="vs. last month"
            icon={fuelIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Total Spend"
            value={formatCurrency(stats.total_vendor_spend?.value)}
            {...(stats.total_vendor_spend?.percentage != null && {
              percentage: `${stats.total_vendor_spend.percentage}`,
            })}
            isIncrease={stats.total_vendor_spend?.is_increase}
            subtitle="vs. last month"
            icon={paymentIcon}
          />
        </div>
      </div>
      <div className="transaction-table">
        <GenericTable
          title="Transaction"
          dataSource={transactionDataSource}
          columns={transactionColumns}
          headerButtonText="Export"
          headerButtonIcon={<FileExcelOutlined />}
          onHeaderButtonClick={handleExport}
        />
      </div>
    </div>
  );
};

export default MasterReport;
